import PropTypes from 'ant-design-vue/es/_util/vue-types'
import { getCurrentInstance, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'

import 'ant-design-vue/es/menu/style'
import Menu from 'ant-design-vue/es/menu'
import 'ant-design-vue/es/icon/style'
import Icon from 'ant-design-vue/es/icon'
import { RightOutlined, DownOutlined, UpOutlined } from '@ant-design/icons-vue'

const {
  Item: MenuItem,
  SubMenu
} = Menu

export const RouteMenuProps = {
  menus: PropTypes.array,
  theme: PropTypes.string.def('dark'),
  mode: PropTypes.string.def('inline'),
  collapsed: PropTypes.bool.def(false),
  i18nRender: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]).def(false)
}

const httpReg = /(http|https|ftp):\/\/([\w.]+\/?)\S*/

const renderMenu = (h, item, i18nRender, router) => {
  if (item && !item.hidden) {
    const bool = item.children && !item.hideChildrenInMenu
    return bool ? renderSubMenu(h, item, i18nRender, router) : renderMenuItem(h, item, i18nRender, router)
  }
  return null
}

const renderSubMenu = (h, item, i18nRender, router) => {
  // 使用 JSX 语法，确保 SubMenu 正确渲染
  // 不设置 popupClassName，确保使用 inline 模式
  return (
    <SubMenu 
      key={item.name}
      title={(
        <span style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%'
        }}>
          {renderIcon(h, item.meta.icon)}
          {renderTitle(h, item.meta.title, i18nRender)}
        </span>
      )}
    >
      {!item.hideChildrenInMenu && item.children.map(cd => renderMenu(h, cd, i18nRender, router))}
    </SubMenu>
  )
}

const renderMenuItem = (h, item, i18nRender, router) => {
  const meta = Object.assign({}, item.meta)
  const target = meta.target || null
  const hasRemoteUrl = httpReg.test(item.path)
  
  // 确定跳转路径，修复双斜杠问题
  let toPath = item.path
  if (toPath && typeof toPath === 'string') {
    // 修复双斜杠问题：将连续的斜杠替换为单个斜杠
    toPath = toPath.replace(/\/+/g, '/')
    // 如果是外部链接，保持原样
    if (!httpReg.test(toPath) && !toPath.startsWith('/')) {
      toPath = '/' + toPath
    }
  } else if (!toPath && item.name) {
    // 如果没有 path，尝试使用 name
    toPath = { name: item.name }
  }
  
  if (item.children && item.hideChildrenInMenu) {
    // 把有子菜单的 并且 父菜单是要隐藏子菜单的
    // 都给子菜单增加一个 hidden 属性
    // 用来给刷新页面时， selectedKeys 做控制用
    item.children.forEach(cd => {
      cd.meta = Object.assign(cd.meta || {}, { hidden: true })
    })
  }
  
  // 添加点击事件处理
  const handleClick = (e) => {
    console.log('renderMenuItem: handleClick', item.path, item.name, toPath)
    
    // 如果是外部链接，不阻止默认行为
    if (hasRemoteUrl || target) {
      return
    }
    
    // 阻止默认行为，使用路由跳转
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    
    // 使用传入的 router，如果没有则尝试从全局获取
    let currentRouter = router
    if (!currentRouter) {
      // 尝试从 window 获取 router（如果已经全局注册）
      if (window.$vueApp && window.$vueApp.config && window.$vueApp.config.globalProperties && window.$vueApp.config.globalProperties.$router) {
        currentRouter = window.$vueApp.config.globalProperties.$router
      } else if (window.$router) {
        currentRouter = window.$router
      }
    }
    
    if (currentRouter && toPath) {
      console.log('renderMenuItem: 准备跳转到', toPath, 'router:', currentRouter ? '存在' : '不存在')
      try {
        currentRouter.push(toPath).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            console.error('renderMenuItem: 路由跳转失败', err)
          } else {
            console.log('renderMenuItem: 路由重复导航，已忽略')
          }
        })
      } catch (error) {
        console.error('renderMenuItem: 路由跳转异常', error)
      }
    } else {
      console.warn('renderMenuItem: router 或 path 未找到', {
        router: currentRouter ? '存在' : '不存在',
        toPath: toPath
      })
    }
  }
  
  // 对于外部链接，使用 <a> 标签
  if (hasRemoteUrl || target) {
    return (
      <MenuItem key={item.name}>
        <a href={item.path} target={target || '_blank'} style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%'
        }}>
          {renderIcon(h, meta.icon)}
          {renderTitle(h, meta.title, i18nRender)}
        </a>
      </MenuItem>
    )
  }
  
  // 对于内部路由，使用点击事件而不是 router-link
  // 因为 router-link 在 MenuItem 内部可能无法正常工作
  return (
    <MenuItem key={item.name} onClick={handleClick}>
      <span style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%'
      }}>
        {renderIcon(h, meta.icon)}
        {renderTitle(h, meta.title, i18nRender)}
      </span>
    </MenuItem>
  )
}

const renderIcon = (h, icon) => {
  if (icon === undefined || icon === 'none' || icon === null) {
    return null
  }
  const props = {}
  typeof (icon) === 'object' ? (props.component = icon) : (props.type = icon)
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      marginRight: '12px',
      fontSize: '16px',
      width: '16px',
      height: '16px',
      justifyContent: 'center'
    }}>
      <Icon {...{ props }} />
    </span>
  )
}

const renderTitle = (h, title, i18nRender) => {
  return (
    <span style={{
      fontSize: '14px',
      lineHeight: '1.5'
    }}>
      { i18nRender && i18nRender(title) || title }
    </span>
  )
}

const RouteMenu = {
  name: 'RouteMenu',
  props: RouteMenuProps,
  data () {
    return {
      openKeys: [],
      selectedKeys: [],
      cachedOpenKeys: [],
      cachedSelectedKeys: []
    }
  },
  render (h) {
    const { mode, theme, menus, i18nRender } = this
    
    // 调试：检查 menus
    console.log('BaseMenu: menus', menus)
    console.log('BaseMenu: menus type', typeof menus, Array.isArray(menus))
    console.log('BaseMenu: menus length', Array.isArray(menus) ? menus.length : 'not array')
    
    const handleOpenChange = (openKeys) => {
      console.log('BaseMenu: handleOpenChange called', {
        newOpenKeys: openKeys,
        currentOpenKeys: this.openKeys,
        mode: mode,
        isArray: Array.isArray(openKeys),
        openKeysType: typeof openKeys
      })
      
      // 强制使用 inline 模式，确保子菜单向下展开
      if (mode === 'horizontal') {
        this.openKeys = Array.isArray(openKeys) ? openKeys : []
        return
      }
      
      // inline 模式：允许多个子菜单同时展开
      // 确保 openKeys 是数组，并创建新数组以确保响应式更新
      const newKeys = Array.isArray(openKeys) ? [...openKeys] : []
      
      // 直接赋值，Vue 会自动处理响应式
      this.openKeys = newKeys
      
      console.log('BaseMenu: openKeys updated', {
        before: this.openKeys,
        after: newKeys,
        length: newKeys.length
      })
      
      // 立即强制更新，确保菜单展开
      this.$nextTick(() => {
        console.log('BaseMenu: after nextTick, openKeys:', this.openKeys)
        // 强制触发视图更新
        if (this.$forceUpdate) {
          this.$forceUpdate()
        }
      })
    }

    // 处理菜单选择事件，触发路由跳转
    const handleSelect = (menu) => {
      console.log('BaseMenu: handleSelect', menu)
      this.$emit('select', menu)
      
      // 如果不是外部链接，更新选中状态
      if (!httpReg.test(menu.key)) {
        this.selectedKeys = menu.selectedKeys
        
        // 查找对应的菜单项，获取路径并跳转
        const findMenuItem = (items, key) => {
          for (const item of items) {
            if (item.name === key || item.key === key) {
              return item
            }
            if (item.children) {
              const found = findMenuItem(item.children, key)
              if (found) return found
            }
          }
          return null
        }
        
        const menuItem = findMenuItem(safeMenus, menu.key)
        if (menuItem && menuItem.path) {
          // 修复路径格式：将双斜杠替换为单斜杠
          let path = menuItem.path
          if (typeof path === 'string') {
            path = path.replace(/^\/+/, '/')
          }
          
          console.log('BaseMenu: 准备跳转到', path)
          // 使用 router 进行跳转
          if (this.$router) {
            this.$router.push(path).catch(err => {
              // 忽略重复导航错误
              if (err.name !== 'NavigationDuplicated') {
                console.error('BaseMenu: 路由跳转失败', err)
              }
            })
          }
        }
      }
    }
    
    // 自定义展开图标函数 - 显示向上/向下箭头
    // 展开时显示向上箭头，折叠时显示向下箭头（表示可以向下展开）
    const expandIcon = (props) => {
      const { isOpen } = props
      const IconComponent = isOpen ? UpOutlined : DownOutlined
      return h('span', {
        style: {
          position: 'absolute',
          right: '16px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'rgba(255, 255, 255, 0.85)',
          fontSize: '12px',
          transition: 'all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '12px',
          height: '12px',
          lineHeight: '12px',
          zIndex: 1
        }
      }, [
        h(IconComponent)
      ])
    }

    // 确保 openKeys 是普通数组，不是 Proxy
    const openKeysArray = Array.isArray(this.openKeys) ? [...this.openKeys] : []
    const selectedKeysArray = Array.isArray(this.selectedKeys) ? [...this.selectedKeys] : []
    
    console.log('BaseMenu: 准备渲染 Menu', {
      mode: 'inline',
      openKeys: openKeysArray,
      selectedKeys: selectedKeysArray,
      openKeysLength: openKeysArray.length
    })
    
    const dynamicProps = {
      props: {
        mode: 'inline', // 强制使用inline模式，确保向下展开
        theme: theme || 'dark',
        openKeys: openKeysArray,
        selectedKeys: selectedKeysArray,
        expandIcon: expandIcon,
        inlineIndent: 24 // 子菜单缩进
      },
      on: {
        select: handleSelect,
        openChange: handleOpenChange,
        'update:openKeys': (val) => {
          console.log('BaseMenu: update:openKeys event', val)
          handleOpenChange(val)
        }
      }
    }

    // 确保 menus 是数组，防止 undefined 错误
    const safeMenus = Array.isArray(menus) ? menus : []
    console.log('BaseMenu: safeMenus length', safeMenus.length)
    
    // 获取 router 实例
    const router = this.$router || (this.$parent && this.$parent.$router)
    
    const menuItems = safeMenus.map(item => {
      if (item.hidden) {
        return null
      }
      return renderMenu(h, item, i18nRender, router)
    })
    
    console.log('BaseMenu: menuItems length', menuItems.length)
    
    console.log('BaseMenu: 渲染菜单, mode:', 'inline', 'openKeys:', this.openKeys, 'selectedKeys:', this.selectedKeys)
    console.log('BaseMenu: menuItems count:', menuItems.filter(item => item !== null).length)
    
    return <Menu {...dynamicProps}>{menuItems}</Menu>
  },
  methods: {
    updateMenu () {
      const names = this.$route.meta.names
      const { hidden } = this.$route.meta
      if (names.length >= 5 && hidden) {
        names.pop()
      }
      this.selectedKeys = [names[names.length - 1]]
      const openKeys = []
      if (this.mode === 'inline') {
        names.forEach(item => {
          item && openKeys.push(item)
        })
      }

      this.collapsed ? (this.cachedOpenKeys = openKeys) : (this.openKeys = openKeys)
    }
  },
  computed: {
    rootSubmenuKeys: vm => {
      const keys = []
      vm.menus.forEach(item => keys.push(item.name))
      return keys
    }
  },
  created () {
    this.$watch('$route', () => {
      this.updateMenu()
    })
    this.$watch('collapsed', val => {
      if (val) {
        this.cachedOpenKeys = this.openKeys.concat()
        this.openKeys = []
      } else {
        this.openKeys = this.cachedOpenKeys
      }
    })
  },
  mounted () {
    this.updateMenu()
  }
}

export default RouteMenu
